import React, { useState, useEffect } from 'react';
import api from '../../api/axios';

const PROVIDERS = [
  {
    key: 'stripe',
    label: 'Stripe',
    desc: 'Card payments — international. Requires a Stripe account.',
    fields: [
      { key: 'secret_key', label: 'Secret Key', type: 'password' },
      { key: 'currency', label: 'Currency', type: 'text', placeholder: 'pkr' },
    ],
  },
  {
    key: 'quickbooks',
    label: 'QuickBooks',
    desc: 'Accounting sync — invoicing, not payment collection.',
    fields: [
      { key: 'realm_id', label: 'Realm ID', type: 'text' },
      { key: 'access_token', label: 'Access Token', type: 'password' },
      { key: 'environment', label: 'Environment', type: 'select', options: ['production', 'sandbox'] },
    ],
  },
  {
    key: 'jazzcash',
    label: 'JazzCash',
    desc: 'Pakistani mobile wallet + card. Parents pay via JazzCash app or card.',
    fields: [
      { key: 'merchant_id', label: 'Merchant ID', type: 'text' },
      { key: 'password', label: 'Password', type: 'password' },
      { key: 'integrity_salt', label: 'Integrity Salt', type: 'password' },
      { key: 'return_url', label: 'Return URL', type: 'text', placeholder: 'https://yourschool.com/pay/return' },
      { key: 'environment', label: 'Environment', type: 'select', options: ['production', 'sandbox'] },
    ],
  },
  {
    key: 'easypaisa',
    label: 'EasyPaisa',
    desc: 'Pakistani mobile wallet. Parents pay via EasyPaisa app or SMS.',
    fields: [
      { key: 'store_id', label: 'Store ID', type: 'text' },
      { key: 'hash_key', label: 'Hash Key', type: 'password' },
      { key: 'return_url', label: 'Return URL', type: 'text' },
      { key: 'environment', label: 'Environment', type: 'select', options: ['production', 'sandbox'] },
    ],
  },
  {
    key: 'paypro',
    label: 'PayPro',
    desc: 'Aggregator — accepts cards, all bank apps, wallets.',
    fields: [
      { key: 'api_key', label: 'API Key', type: 'password' },
      { key: 'return_url', label: 'Return URL', type: 'text' },
      { key: 'environment', label: 'Environment', type: 'select', options: ['production', 'sandbox'] },
    ],
  },
  {
    key: 'neem',
    label: 'Neem',
    desc: 'Aggregator — cards, JazzCash, EasyPaisa, RAAST.',
    fields: [
      { key: 'api_key', label: 'API Key', type: 'password' },
      { key: 'api_secret', label: 'API Secret', type: 'password' },
      { key: 'return_url', label: 'Return URL', type: 'text' },
      { key: 'environment', label: 'Environment', type: 'select', options: ['production', 'sandbox'] },
    ],
  },
  {
    key: 'webhook',
    label: 'Custom Webhook / KuickPay / 1BILL',
    desc: 'For providers that expect a simple POST — KuickPay, 1BILL, or your own endpoint.',
    fields: [
      { key: 'url', label: 'Endpoint URL', type: 'text', placeholder: 'https://...' },
      { key: 'api_key', label: 'API Key / Biller ID', type: 'password' },
      { key: 'callback_url', label: 'Callback URL', type: 'text' },
      { key: 'webhook_secret', label: 'Webhook Secret (for inbound)', type: 'password' },
    ],
  },
  {
    key: 'excel',
    label: 'Excel Export (built-in)',
    desc: 'No configuration. Download a spreadsheet of all fees.',
    fields: [],
  },
];

export default function IntegrationsPanel() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [config, setConfig] = useState({});

  useEffect(() => { api.get('/integrations/').then(r => setList(r.data)); }, []);

  const refresh = async () => {
    const r = await api.get('/integrations/');
    setList(r.data);
  };

  const save = async (provider) => {
    try {
      await api.post('/integrations/', {
        provider: provider.key,
        display_name: provider.label,
        config,
        is_active: true,
      });
      setEditing(null);
      setConfig({});
      refresh();
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  const deactivate = async (providerKey) => {
    if (!window.confirm(`Deactivate ${providerKey}?`)) return;
    await api.delete(`/integrations/${providerKey}`);
    refresh();
  };

  const downloadExcel = () => {
    const token = localStorage.getItem('token');
    fetch('/api/integrations/export/fees.xlsx', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => res.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'fees.xlsx';
        a.click();
        URL.revokeObjectURL(url);
      });
  };

  const renderField = (field) => {
    if (field.type === 'select') {
      return (
        <select
          key={field.key}
          className="w-full p-2 border rounded text-sm"
          value={config[field.key] || field.options[0]}
          onChange={e => setConfig({ ...config, [field.key]: e.target.value })}
        >
          {field.options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    }
    return (
      <input
        key={field.key}
        type={field.type}
        className="w-full p-2 border rounded text-sm"
        placeholder={field.placeholder || field.label}
        value={config[field.key] || ''}
        onChange={e => setConfig({ ...config, [field.key]: e.target.value })}
      />
    );
  };

  return (
    <div className="bg-white p-6 rounded shadow">
      <h2 className="text-xl font-bold mb-4">🔌 Fee Integrations</h2>
      <p className="text-sm text-gray-500 mb-4">
        Only one provider can be active at a time. Configure a provider, then use the
        <strong> Sync </strong> button on a fee row to push it.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {PROVIDERS.map(p => {
          const existing = list.find(x => x.provider === p.key);
          const isEditing = editing === p.key;
          return (
            <div key={p.key} className="border rounded p-4">
              <div className="flex justify-between items-start mb-2 gap-2">
                <div className="flex-1">
                  <h3 className="font-semibold">{p.label}</h3>
                  <p className="text-xs text-gray-500 mt-0.5">{p.desc}</p>
                </div>
                <span className={`text-xs px-2 py-1 rounded whitespace-nowrap ${
                  existing?.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                }`}>
                  {existing?.is_active ? 'Active' : 'Not configured'}
                </span>
              </div>

              {isEditing ? (
                <div className="space-y-2 mt-3">
                  {p.fields.length === 0 ? (
                    <p className="text-xs text-gray-500 italic">
                      No configuration needed.
                    </p>
                  ) : (
                    p.fields.map(f => (
                      <div key={f.key}>
                        <label className="block text-xs font-medium text-gray-600 mb-0.5">
                          {f.label}
                        </label>
                        {renderField(f)}
                      </div>
                    ))
                  )}
                  <div className="flex gap-2 pt-1">
                    <button
                      className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                      onClick={() => save(p)}
                    >
                      Save
                    </button>
                    <button
                      className="px-3 py-1.5 bg-gray-200 rounded text-sm hover:bg-gray-300"
                      onClick={() => { setEditing(null); setConfig({}); }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-wrap gap-3 mt-3">
                  <button
                    className="text-sm text-blue-600 hover:underline"
                    onClick={() => { setEditing(p.key); setConfig({}); }}
                  >
                    {existing ? 'Update' : 'Configure'}
                  </button>
                  {existing?.is_active && p.key !== 'excel' && (
                    <button
                      className="text-sm text-red-600 hover:underline"
                      onClick={() => deactivate(p.key)}
                    >
                      Disable
                    </button>
                  )}
                  {p.key === 'excel' && (
                    <button
                      className="text-sm text-green-600 hover:underline"
                      onClick={downloadExcel}
                    >
                      Download .xlsx
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}